/**
 * Composable для управления отображением заказов
 *
 * Отвечает за:
 * - Computed свойства для UI
 * - Обработку кликов и взаимодействий
 * - Загрузку деталей заказов
 */

import { computed, nextTick, type Ref } from 'vue'
import { consola } from 'consola'
import type { OrderSummary } from '@entities/order'
import { getStatusBadgeClass, getStatusMessage } from '@entities/order'
import { ORDER_DISPLAY_CONFIG } from '@shared/config'

const logger = consola.withTag('order-display')

/**
 * Интерфейс зависимостей
 */
interface OrderDisplayDeps {
  unpaidOrdersStore: any
  selectOrder: (orderId: string) => void
  openOrderDetailsModal: () => Promise<void>
  slideToOrder: (orderId: string, speed: number) => Promise<void>
  currentSlideIndex: Ref<number>
  isSwiperUpdating: Ref<boolean>
  navigationOrders: Ref<OrderSummary[]>
}

/**
 * Composable для отображения заказов
 */
export function useOrderDisplay(deps: OrderDisplayDeps) {
  const {
    unpaidOrdersStore,
    selectOrder,
    openOrderDetailsModal,
    slideToOrder,
    currentSlideIndex,
    isSwiperUpdating,
    navigationOrders,
  } = deps

  // Computed
  const order = computed(() => unpaidOrdersStore.selectedOrder)
  const orderId = computed(() => order.value?.id)

  /**
   * Проверяет, нужно ли показывать блок с заказами
   */
  const shouldShowOrderBlock = computed(() => {
    return unpaidOrdersStore.summaries?.length > 0
  })

  /**
   * Получает CSS класс бейджа для заказа
   */
  function getBadgeClass(orderSummary: OrderSummary): string {
    return getStatusBadgeClass(orderSummary)
  }

  /**
   * Получает текстовое сообщение статуса для заказа
   */
  function getMessage(orderSummary: OrderSummary): string {
    return getStatusMessage(orderSummary)
  }

  /**
   * Форматирует ID заказа для отображения
   * Использует orderPosId если он доступен в полных деталях заказа
   */
  function formatOrderId(orderSummary: OrderSummary): string {
    if (!orderSummary?.id) {
      return ORDER_DISPLAY_CONFIG.DEFAULT_ORDER_ID
    }

    const orderId = String(orderSummary.id)

    // Пытаемся получить полные детали заказа из кэша
    const fullOrder = unpaidOrdersStore.getDetailsById(orderId)

    // Если есть полный заказ с orderPosId, используем его
    if (fullOrder?.orderPosId) {
      return String(fullOrder.orderPosId)
    }

    // Иначе используем обычный id
    return orderId
  }

  /**
   * Обрабатывает клик по заказу
   */
  async function handleOrderClick(orderSummary?: OrderSummary): Promise<void> {
    try {
      if (!orderSummary?.id) {
        await openOrderDetailsModal()
        return
      }

      const targetId = String(orderSummary.id)

      // Обновляем выбранный заказ, если отличается
      if (String(orderId.value ?? '') !== targetId) {
        selectOrder(targetId)
        await nextTick()
      }

      // Переключаем swiper на нужный слайд
      const targetIndex = navigationOrders.value.findIndex(
        (o) => String(o.id) === targetId,
      )

      if (targetIndex >= 0 && currentSlideIndex.value !== targetIndex && !isSwiperUpdating.value) {
        await slideToOrder(targetId, 0)
      }

      // Загружаем детали если нужно
      if (!unpaidOrdersStore.hasDetails(targetId)) {
        await unpaidOrdersStore.loadOrderDetails(targetId)
      }

      // Открываем модалку
      await openOrderDetailsModal()
    } catch (error) {
      logger.error('Failed to handle order click:', error)
    }
  }

  /**
   * Обрабатывает изменение слайда в swiper
   */
  async function handleSlideChange(activeIndex: number): Promise<void> {
    try {
      currentSlideIndex.value = activeIndex

      const activeOrder = navigationOrders.value[activeIndex]
      if (!activeOrder) return

      // Загружаем полные данные если их нет
      const orderId = String(activeOrder.id)
      if (!unpaidOrdersStore.hasDetails(orderId)) {
        await unpaidOrdersStore.loadOrderDetails(orderId)
      }

      // Переключаемся на заказ
      selectOrder(orderId)
    } catch (error) {
      logger.error('Failed to handle slide change:', error)
    }
  }

  /**
   * Загружает детали заказа при необходимости
   */
  async function ensureOrderDetailsLoaded(orderId: string): Promise<void> {
    if (!unpaidOrdersStore.hasDetails(orderId)) {
      await unpaidOrdersStore.loadOrderDetails(orderId)
    }
  }

  return {
    // Computed
    order,
    orderId,
    shouldShowOrderBlock,

    // Methods
    getBadgeClass,
    getMessage,
    formatOrderId,
    handleOrderClick,
    handleSlideChange,
    ensureOrderDetailsLoaded,
  }
}
